import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import { LoadingButton } from "@mui/lab";
import { downloadAsXLS } from "src/utils";
import FilterDrawer from "./filters";
import {
  useChangeReorderRequestsMutation,
  useDownloadReordersRequests,
  useGetReorderRequestsList,
  useUpdateReorderRequestItemMutation,
} from "src/api/inventory/reorder";
import { useGetSuppliersList } from "src/api/purchasing/suppliers";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import SecondaryChip from "src/shared/components/secondary-chip";

const statusTypes = [
  {
    label: "New",
    value: "new",
  },
  {
    label: "Approved",
    value: "approved",
  },
  {
    label: "Cancelled",
    value: "cancelled",
  },
];

type actionTypes = "approve" | "cancel";

const ApprovalTable = () => {
  const { t } = useTranslation("purchasing");
  const { t: inventoryT } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const [open, setOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [type, setType] = useState("new");
  const [actionType, setActionType] = useState<actionTypes>("approve");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [allRequests, setAllRequests] = useState<any[]>();
  const allSelected = !!allRequests?.every((item) => item.selected);
  const noneSelected = !!allRequests?.every((item) => !item.selected);

  const HeadText = [
    generalT("branch"),
    t("name"),
    t("SKU"),
    inventoryT("currentLevel"),
    inventoryT("reorder"),
    inventoryT("currentSupplier"),
    inventoryT("cheapestSupplier"),
    generalT("status"),
    inventoryT("poReference"),
    inventoryT("reorderBy"),
    inventoryT("reorderAt"),
  ];

  // APIS
  const { data: suppliersData } = useGetSuppliersList({});
  const { data, refetch, isFetching } = useGetReorderRequestsList({
    ...(!!name && { name }),
    ...(!!sku && { sku }),
    ...(!!type && { status: type }),
  });
  const {
    data: downloadedData,
    refetch: refetchDownload,
    isFetching: isFetchingDownload,
    status: downloadStatus,
  } = useDownloadReordersRequests({
    ...(!!name && { name }),
    ...(!!sku && { sku }),
    ...(!!type && { status: type }),
  });

  const { mutate, status, isPending, error } =
    useChangeReorderRequestsMutation();

  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateReorderRequestItemMutation();

  // permissions
  // const addPermission = oneBranchHasPermission(InventoryPermissions.addItem);

  const handleOpen = (type: actionTypes) => {
    setActionType(type);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleDownload = () => {
    refetchDownload();
  };

  const emptyDataArr = data?.data.length === 0;

  const handleChangeOrder = (id: number) => {
    setAllRequests((prevOrders) =>
      prevOrders?.map((order: any) =>
        order.id === id ? { ...order, selected: !order.selected } : order
      )
    );
  };

  const handleSelectAllChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAllRequests((prevOrders) =>
      prevOrders?.map((order) => ({ ...order, selected: e.target.checked }))
    );
  };

  const handleApproveRequest = () => {
    mutate({
      status: actionType === "approve" ? "approved" : "cancelled",
      items: (allRequests ?? [])
        ?.filter((item) => item.selected && item.status === "new")
        .map((item) => item.id),
    });
  };

  useEffect(() => {
    if (data) {
      setAllRequests(
        data.data.map((item: any) => {
          return { ...item, selected: false };
        })
      );
    }
  }, [data]);

  useEffect(() => {
    if (downloadStatus === "success" && !!downloadedData) {
      const utf8CSVData = `\uFEFF${downloadedData}`;
      downloadAsXLS(utf8CSVData, "reorder-requests");
    }
  }, [isFetchingDownload]);

  useEffect(() => {
    if (status === "success") {
      refetch();
      handleClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Stack spacing={3} maxWidth={{ xs: "100%", md: `calc(100vw - 316px)` }}>
      <Box display={"flex"} justifyContent={"flex-end"}>
        <Stack
          direction={"row"}
          gap={2}
          flexWrap={"wrap"}
          mt={{ xs: 2, md: 5, lg: 0 }}
        >
          <Button
            color="tertiary"
            variant="outlined"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
            onClick={handleOpenFilter}
          >
            {t("filter")}
          </Button>
          <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="demo-simple-select-label">
                {t("status")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label={t("status")}
                size="small"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {statusTypes.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <LoadingButton
            sx={{
              border: "1px solid #98A2B3",
              height: "40px",
              fontWeight: 500,
              borderRadius: "4px",
              padding: "7px 10px",
              background: "#FCFCFD",
              fontSize: "16px",
            }}
            color="tertiary"
            variant="outlined"
            endIcon={<img src={DownloadIcon} alt="" />}
            loading={isFetchingDownload}
            onClick={handleDownload}
          >
            {t("download")}
          </LoadingButton>
          {type === "new" && (
            <>
              <Button
                variant="contained"
                onClick={() => handleOpen("cancel")}
                color="error"
                disabled={noneSelected}
              >
                {inventoryT("cancelSelected")}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleOpen("approve")}
                disabled={noneSelected}
              >
                {inventoryT("approveSelected")}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell
                align={"center"}
                sx={{
                  bgcolor: "#F9FAFB",
                }}
              >
                {type === "new" && (
                  <Stack direction={"row"} alignItems={"center"}>
                    <Checkbox
                      checked={allSelected}
                      onChange={handleSelectAllChange}
                      disabled={type !== "new"}
                    />
                    <Typography>{inventoryT("selectAll")}</Typography>
                  </Stack>
                )}
              </TableCell>
              {HeadText.map((item) => (
                <TableCell
                  key={item}
                  align={"center"}
                  sx={{
                    bgcolor: "#F9FAFB",
                  }}
                >
                  <Typography
                    fontSize={"12px"}
                    color={"#475467"}
                    lineHeight={"18px"}
                    fontWeight={500}
                  >
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isFetching || !allRequests ? (
            <TableLoadingSkeleton rowsLength={15} cellsLength={12} />
          ) : (
            <TableBody>
              {allRequests.map((row: any, index) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align={"center"}>
                    {type === "new" && (
                      <Checkbox
                        checked={row.selected}
                        onChange={(e) => {
                          handleChangeOrder(row.id);
                        }}
                        disabled={type !== "new"}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">{row.branch?.name}</TableCell>
                  <TableCell align="center">{row.name}</TableCell>
                  <TableCell align="center">{row.sku}</TableCell>
                  <TableCell align="center">
                    {row.current_quantity ?? "--"}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      sx={{ width: "100px" }}
                      defaultValue={row.quantity}
                      onChange={(e) => {
                        mutateUpdate({
                          id: row.id,
                          quantity: Number(e.target.value),
                        });
                      }}
                      disabled={type !== "new"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Select
                      size="small"
                      sx={{ width: "100px" }}
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      defaultValue={row.supplier?.id}
                      onChange={(e) => {
                        mutateUpdate({
                          id: row.id,
                          supplier_id: Number(e.target.value),
                        });
                      }}
                      disabled={type !== "new"}
                    >
                      {suppliersData?.data?.map((sup: any) => (
                        <MenuItem key={sup.id} value={sup.id}>
                          {sup.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center">
                    {row.cheapest_supplier?.name}
                  </TableCell>
                  <TableCell align="center">
                    <SecondaryChip
                      label={row.status}
                      variant={
                        row.status === "new"
                          ? "warning"
                          : row.status === "approved"
                          ? "success"
                          : "error"
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {row.po_reference ?? "--"}
                  </TableCell>
                  <TableCell align="center">{row.created_by?.name}</TableCell>
                  <TableCell align="center">{row.created_at}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {emptyDataArr && <NoData />}
      </TableContainer>

      <FilterDrawer
        open={openFilter}
        name={name}
        setName={setName}
        sku={sku}
        setSku={setSku}
        type={type}
        setType={setType}
        handleClose={handleCloseFilter}
      />

      <ConfirmPopup
        open={open}
        item={""}
        handleClose={handleClose}
        handleConfirm={handleApproveRequest}
        noIcon
        isPending={isPending}
        title={inventoryT(
          actionType === "approve" ? "approveSelected" : "cancelSelected"
        )}
        subtitle={generalT("areYouSureToConfirm")}
        confirmBtnText={generalT("confirm")}
      />
    </Stack>
  );
};
export default ApprovalTable;
